// Direct paths, not the feature barrel — the barrel re-exports hooks that import this module back.
import {buyerKeys} from '@/features/buyers/hooks/useBuyersQuery';
import {paymentKeys} from '@/features/buyers/hooks/usePaymentsQuery';
import {purchaseSummaryKeys} from '@/features/buyers/hooks/usePurchaseSummaryQuery';
import {payoutInstructionKeys} from '@/features/payments/hooks/usePayoutInstructionsQuery';

import {trustAccountActivityKeys} from '../hooks/useTrustAccountActivity';
import {trustAccountKeys} from '../hooks/useTrustAccounts';

import type {QueryClient} from '@tanstack/react-query';

/** Resolves once the team is refetched; detail, lists and the suggested team refresh in the background. */
export function invalidateTrustAccountTeam(
  queryClient: QueryClient,
  trustAccountId: string
): Promise<void> {
  void queryClient.invalidateQueries({queryKey: trustAccountKeys.detail(trustAccountId)});
  void queryClient.invalidateQueries({queryKey: trustAccountKeys.lists()});
  void queryClient.invalidateQueries({queryKey: trustAccountKeys.suggestedTeam()});
  return queryClient.invalidateQueries({queryKey: trustAccountKeys.members(trustAccountId)});
}

/** A user's firm role sets which account roles they can hold, so every account's team data goes stale. */
export function invalidateAllTrustAccountTeams(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({queryKey: trustAccountKeys.allMembers()});
  void queryClient.invalidateQueries({queryKey: trustAccountKeys.details()});
  void queryClient.invalidateQueries({queryKey: trustAccountKeys.lists()});
}

export function invalidateTrustFinancials(queryClient: QueryClient, trustAccountId: string): void {
  void queryClient.invalidateQueries({queryKey: trustAccountKeys.balance(trustAccountId)});
  void queryClient.invalidateQueries({queryKey: trustAccountKeys.balances()});
  void queryClient.invalidateQueries({queryKey: trustAccountKeys.transactions(trustAccountId)});
  void queryClient.invalidateQueries({queryKey: trustAccountActivityKeys.all(trustAccountId)});
}

/**
 * Buyer payment instructions changed — also invalidates the purchase summary derived from them,
 * and every buyer list/detail query for this trust account, since a buyer's `canDelete` flag is
 * derived from their payment-instruction state (no payins, no active instruction).
 */
export function invalidatePaymentInstructions(
  queryClient: QueryClient,
  trustAccountId: string
): void {
  void queryClient.invalidateQueries({queryKey: paymentKeys.all(trustAccountId)});
  void queryClient.invalidateQueries({queryKey: purchaseSummaryKeys.detail(trustAccountId)});
  void queryClient.invalidateQueries({queryKey: buyerKeys.lists(trustAccountId)});
  void queryClient.invalidateQueries({queryKey: buyerKeys.details(trustAccountId)});
}

/** A payout's amount counts against the available balance, so its list and the financials move together. */
export function invalidatePayoutInstructions(
  queryClient: QueryClient,
  trustAccountId: string
): void {
  invalidateTrustFinancials(queryClient, trustAccountId);
  void queryClient.invalidateQueries({queryKey: payoutInstructionKeys.lists(trustAccountId)});
}
